/**
 * Dashboard Page Component
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAudio } from '../contexts/AudioContext';
import { authAPI, utilityAPI, songsAPI } from '../services/api';
import AudioPlayer from '../components/AudioPlayer';
import SongList from '../components/SongList';
import ImportModal from '../components/ImportModal';
import StatsModal from '../components/StatsModal';
import DownloadStatus from '../components/DownloadStatus';
import AdminSettingsModal from '../components/AdminSettingsModal';
import RecentlyPlayed from '../components/RecentlyPlayed';
import MiniPlayer from '../components/MiniPlayer';
import useKeyboardShortcuts from '../hooks/useKeyboardShortcuts';
import './DashboardPage.css';

const DashboardPage = () => {
  const navigate = useNavigate();
  const { resumePlaybackState, playbackStateSynced, playSong, currentSong } = useAudio();
  const [showImportModal, setShowImportModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showDownloadStatus, setShowDownloadStatus] = useState(false);
  const [showAdminSettings, setShowAdminSettings] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [stats, setStats] = useState(null);
  const [user, setUser] = useState(null);
  const [allSongs, setAllSongs] = useState([]);
  const [autoPlayTriggered, setAutoPlayTriggered] = useState(false);
  const [lastPlaybackState, setLastPlaybackState] = useState(null);

  // Enable keyboard shortcuts
  useKeyboardShortcuts();

  // Load initial data and sync playback state
  useEffect(() => {
    loadUserData();
    loadStats();
    loadAllSongs();
    loadLastPlaybackState();

    // Resume playback state if available
    if (!playbackStateSynced) {
      resumePlaybackState();
    }
  }, []);

  const loadLastPlaybackState = async () => {
    try {
      const response = await utilityAPI.getRecentlyPlayed();
      if (response.data && response.data.recently_played && response.data.recently_played.length > 0) {
        const lastPlayed = response.data.recently_played[0];
        setLastPlaybackState(lastPlayed);
      }
    } catch (error) {
      console.error('Failed to load last playback state:', error);
    }
  };

  // Auto-play logic: resume last position or start first song
  useEffect(() => {
    if (playbackStateSynced && !autoPlayTriggered && allSongs.length > 0) {
      // If no current song is set, automatically start the first available song
      if (!currentSong) {
        // Prioritize completed songs, then take the first available song
        const firstCompletedSong = allSongs.find(song => song.download_status === 'completed');
        const firstSong = firstCompletedSong || allSongs[0];

        if (firstSong) {
          console.log('Auto-playing first song:', firstSong.title, 'status:', firstSong.download_status);

          // For non-completed songs, we can't use playSong - need to trigger YouTube embed
          if (firstSong.download_status !== 'completed') {
            // Import YouTubeEmbed component dynamically to show embed
            console.log('Triggering YouTube embed for auto-play');
            // We'll set a global flag or use a different approach
            setAutoPlayTriggered(true);
            return;
          }

          const songIndex = allSongs.findIndex(s => s.id === firstSong.id);
          playSong(firstSong, allSongs, songIndex);
          setAutoPlayTriggered(true);
        }
      } else {
        // Already have a current song from resumePlaybackState
        setAutoPlayTriggered(true);
      }
    }
  }, [playbackStateSynced, allSongs, autoPlayTriggered, currentSong, playSong]);

  const loadAllSongs = async () => {
    try {
      const response = await songsAPI.getSongs();
      if (response.data && response.data.songs) {
        setAllSongs(response.data.songs);
      }
    } catch (error) {
      console.error('Failed to load songs for auto-play:', error);
    }
  };

  const loadUserData = async () => {
    try {
      const response = await authAPI.getCurrentUser();
      setUser(response.data);
    } catch (error) {
      console.error('Failed to load user data:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await utilityAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if logout fails, redirect to login
      navigate('/login');
    }
  };

  const handleImportSuccess = (results) => {
    // Refresh stats after import
    loadStats();
    // Close modal after 2 seconds (handled in ImportModal component)
  };

  const handleRefreshSongs = () => {
    // This will trigger a re-fetch of songs in the SongList component
    window.location.reload();
  };

  const handleClearAll = async () => {
    if (window.confirm('Are you sure you want to clear all songs from the dashboard? This will archive all visible songs but keep their metadata for future reference.')) {
      try {
        const response = await songsAPI.archiveAllSongs();
        if (response.data) {
          alert(`Successfully archived ${response.data.archived_count} songs`);
          // Refresh the song list
          handleRefreshSongs();
          // Update stats
          loadStats();
        }
      } catch (error) {
        console.error('Failed to clear all songs:', error);
        alert('Failed to clear songs. Please try again.');
      }
    }
  };

  const handleContinueListening = async () => {
    if (!lastPlaybackState) return;

    try {
      // Find the song in all songs
      const song = allSongs.find(s => s.id === lastPlaybackState.id);
      if (song) {
        // Get playback state to find position
        const stateResponse = await utilityAPI.getPlaybackState();
        const position = stateResponse.data?.position || 0;
        const isPlaying = stateResponse.data?.is_playing || false;

        // Play the song from the saved position
        const songIndex = allSongs.findIndex(s => s.id === song.id);
        await playSong(song, allSongs, songIndex);

        // Seek to the saved position
        if (position > 0) {
          // We need to seek after the song loads
          setTimeout(() => {
            const audio = document.querySelector('audio');
            if (audio) {
              audio.currentTime = position;
            }
          }, 500);
        }
      }
    } catch (error) {
      console.error('Failed to continue listening:', error);
    }
  };

  const activeFilter = showFavoritesOnly ? 'favorites' : filterStatus;

  return (
    <div className="dashboard-page">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <h1>🎵 Music Library</h1>
            <p>Welcome back, {user?.username || 'User'}!</p>
            <small className="build-info">Built: {new Date().toLocaleString()}</small>
          </div>

          <div className="header-right">
            <div className="stats-summary">
              {stats && (
                <>
                  <div className="stat-item">
                    <span className="stat-value">{stats.total_songs}</span>
                    <span className="stat-label">Total Songs</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{stats.total_favorites}</span>
                    <span className="stat-label">Favorites</span>
                  </div>
                </>
              )}
            </div>

            <button className="btn-stats" onClick={() => setShowStatsModal(true)} title="View Statistics">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M21 21H4.6c-.6 0-1.1-.4-1.2-1L2 4c-.1-.6.4-1 1-1h18c.6 0 1 .4 1 1v16c0 .6-.4 1-1 1zM7 10l2.5 5 2.5-5 2.5 5 2.5-5"/>
              </svg>
            </button>

            <button className="btn-downloads" onClick={() => setShowDownloadStatus(true)} title="Download Status">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>
              </svg>
            </button>

            <button className="btn-admin" onClick={() => setShowAdminSettings(true)} title="Admin Settings">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
            </button>

            <button className="btn-logout" onClick={handleLogout} title="Logout">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/>
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Continue Listening Hero Section */}
        {lastPlaybackState && (
          <div className="continue-listening-hero">
            <div className="continue-content">
              <div className="continue-info">
                <h2>🎧 Continue Listening</h2>
                <p className="continue-song">{lastPlaybackState.title}</p>
                <p className="continue-artist">{lastPlaybackState.artist || 'Unknown Artist'}</p>
                {lastPlaybackState.last_played && (
                  <p className="continue-time">
                    Last played: {new Date(lastPlaybackState.last_played).toLocaleDateString()}
                  </p>
                )}
              </div>
              <button
                className="btn-continue-listening"
                onClick={handleContinueListening}
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z"/>
                </svg>
                Continue
              </button>
            </div>
          </div>
        )}

        <div className="content-area">
          {/* Recently Played Sidebar */}
          <aside className="dashboard-sidebar">
            <RecentlyPlayed showInSidebar={true} />
          </aside>

          {/* Main Content Area */}
          <div className="main-content">
            {/* Controls Bar */}
            <div className="controls-bar">
            <div className="search-section">
              <input
                type="text"
                placeholder="Search songs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="filter-section">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Songs</option>
                <option value="completed">Downloaded</option>
                <option value="pending">Pending</option>
                <option value="downloading">Downloading</option>
                <option value="failed">Failed</option>
              </select>

              <button
                className={`btn-filter ${showFavoritesOnly ? 'active' : ''}`}
                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                title="Show favorites only"
              >
                <svg viewBox="0 0 24 24" fill={showFavoritesOnly ? 'currentColor' : 'none'} stroke="currentColor">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
                Favorites
              </button>

              <button
                className="btn-refresh"
                onClick={handleRefreshSongs}
                title="Refresh song list"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M23 4v6h-6M1 20v-6h6M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                </svg>
              </button>

              <button
                className="btn-clear-all"
                onClick={handleClearAll}
                title="Clear all songs (archives them)"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M10 11v6M14 11v6"/>
                </svg>
                Clear All
              </button>

              <button
                className="btn-import"
                onClick={() => setShowImportModal(true)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
                </svg>
                Import URLs
              </button>
            </div>
          </div>

          {/* Song List */}
          <SongList
            search={searchQuery}
            status={filterStatus === 'all' ? '' : filterStatus}
            favoritesOnly={showFavoritesOnly}
          />
          </div>
        </div>
      </main>

      {/* Audio Player Footer */}
      <footer className="dashboard-footer">
        <AudioPlayer />
      </footer>

      {/* Import Modal */}
      {showImportModal && (
        <ImportModal
          onClose={() => setShowImportModal(false)}
          onImportSuccess={handleImportSuccess}
        />
      )}

      {/* Stats Modal */}
      {showStatsModal && (
        <StatsModal onClose={() => setShowStatsModal(false)} />
      )}

      {/* Download Status Modal */}
      {showDownloadStatus && (
        <DownloadStatus onClose={() => setShowDownloadStatus(false)} />
      )}

      {/* Admin Settings Modal */}
      {showAdminSettings && (
        <AdminSettingsModal onClose={() => setShowAdminSettings(false)} />
      )}

      {/* Mini Player */}
      <MiniPlayer />
    </div>
  );
};

export default DashboardPage;